import { useMutation } from '@apollo/client';
import type { DataExportTestScheduleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DataExportTestScheduleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useTestDataExportScheduleTRPC } from './useTestDataExportScheduleTRPC';

/**
 * Flag-gated wrapper for the `dataExportTestSchedule` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `DataExportTestScheduleMutation` shape.
 *
 * Both paths REJECT on failure (the Apollo branch passes no `onError`, which
 * would otherwise swallow the error), so the caller's try/catch drives the
 * success/failure toasts identically on either path.
 *
 * NOTE: the test run is dispatch-only on both paths — resolving means the export
 * was kicked off, not that it succeeded. Its outcome lands later as an execution
 * history row.
 */
export const useTestDataExportSchedule = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [testScheduleGraphQL, graphqlState] = useMutation(
    DataExportTestScheduleDocument
  );

  const trpcMutation = useTestDataExportScheduleTRPC();

  const testDataExportSchedule = async (
    scheduleId: string
  ): Promise<DataExportTestScheduleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.testDataExportSchedule(scheduleId);
    }

    const result = await testScheduleGraphQL({
      variables: { object: { scheduleId } },
    });
    if (!result.data) {
      throw new Error('Failed to test data export schedule');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      testDataExportSchedule,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    testDataExportSchedule,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
